import React, { useState, useMemo, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FilterSection } from "../../../components/patients/FilterSection";
import { PatientListTable } from "../../../components/patients/PatientListTable";
import { FilterModal } from "../../../components/patients/FilterModal";
import { useToast } from "@/hooks/use-toast";
import { usePatientDetailsForGroups } from "@/hooks/patient/usePatientDetailsForGroups";
import { usePatientGroups } from "@/hooks/activity/usePatientGroups";
import { useAuth } from "@/contexts/AuthContext";
import { groupsGraphqlService } from "@/services/api/groups/groupsGraphqlService";
import axios from "axios";
import { API_CONFIG } from "@/config/envConfig";

export default function DuplicatePatientGroup() {
  const { patientGroupId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { user, token } = useAuth();
  const { patientGroups } = usePatientGroups();

  // Group name state
  const [newGroupName, setNewGroupName] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  // Filter states
  const [nameValue, setNameValue] = useState("");
  const [phoneValue, setPhoneValue] = useState("");
  const [emrValue, setEmrValue] = useState("");
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<any>({});

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPatients, setSelectedPatients] = useState<string[]>([]);

  // Track changes for the new group
  const [removedPatientIds, setRemovedPatientIds] = useState<string[]>([]);
  const [addedPatientIds, setAddedPatientIds] = useState<string[]>([]);
  const [addedPatients, setAddedPatients] = useState<any[]>([]);

  const itemsPerPage = 10;

  // Get current group data
  const currentGroup = useMemo(() => {
    return patientGroups.find(g => g.patient_group_id.toString() === patientGroupId);
  }, [patientGroups, patientGroupId]);

  // Fetch patients from the original group
  const { 
    patients, 
    isLoading, 
    totalCount,
    refetch 
  } = usePatientDetailsForGroups(
    itemsPerPage, 
    (currentPage - 1) * itemsPerPage,
    patientGroupId ? parseInt(patientGroupId) : undefined
  );

  // Set initial group name suggestion
  useEffect(() => {
    if (currentGroup && !newGroupName) {
      setNewGroupName(`${currentGroup.group_name} (Copy)`);
    }
  }, [currentGroup]);

  // Load session storage data on mount and when location changes (navigation)
  useEffect(() => {
    const storedRemoved = sessionStorage.getItem(`duplicate_${patientGroupId}_removed`);
    const storedAdded = sessionStorage.getItem(`duplicate_${patientGroupId}_added`);
    const storedAddedPatients = sessionStorage.getItem(`duplicate_${patientGroupId}_added_patients`);
    
    if (storedRemoved) {
      setRemovedPatientIds(JSON.parse(storedRemoved));
    }
    if (storedAdded) {
      setAddedPatientIds(JSON.parse(storedAdded));
    }
    if (storedAddedPatients) {
      setAddedPatients(JSON.parse(storedAddedPatients));
    }
  }, [patientGroupId, location]);

  // Save changes to session storage
  useEffect(() => {
    if (patientGroupId) {
      sessionStorage.setItem(`duplicate_${patientGroupId}_removed`, JSON.stringify(removedPatientIds));
      sessionStorage.setItem(`duplicate_${patientGroupId}_added`, JSON.stringify(addedPatientIds));
      sessionStorage.setItem(`duplicate_${patientGroupId}_added_patients`, JSON.stringify(addedPatients));
    }
  }, [removedPatientIds, addedPatientIds, addedPatients, patientGroupId]);

  // Transform patients for display (excluding removed ones and including added ones)
  const displayPatients = useMemo(() => {
    const existingPatients = patients
      .filter(patient => !removedPatientIds.includes(patient.enrollment_patient_id?.toString() || ''))
      .map(patient => ({
        id: patient.enrollment_patient_id?.toString() || '',
        patientName: `${patient.first_name || ''} ${patient.middle_name || ''} ${patient.last_name || ''}`.trim(),
        emr: patient.emr_id || 'N/A',
        gender: patient.gender || 'N/A',
        dateOfBirth: patient.dob || 'N/A',
        age: patient.dob ? Math.floor((new Date().getTime() - new Date(patient.dob).getTime()) / 31557600000) : 0,
        phoneNumber: patient.mobile_number || patient.home_number || 'N/A',
        providerDetails: patient.provider_name || 'N/A',
        clinicDetails: patient.clinic_name || 'N/A',
        primaryDx: 'N/A',
        secondaryDx: 'N/A',
        insuranceType: 'N/A',
      }));

    const newlyAddedPatients = addedPatients.map(patient => ({
      id: patient.enrollment_patient_id?.toString() || '',
      patientName: `${patient.first_name || ''} ${patient.middle_name || ''} ${patient.last_name || ''}`.trim(),
      emr: patient.emr_id || 'N/A',
      gender: patient.gender || 'N/A',
      dateOfBirth: patient.dob || 'N/A',
      age: patient.dob ? Math.floor((new Date().getTime() - new Date(patient.dob).getTime()) / 31557600000) : 0,
      phoneNumber: patient.mobile_number || patient.home_number || 'N/A',
      providerDetails: patient.provider_name || 'N/A',
      clinicDetails: patient.clinic_name || 'N/A',
      primaryDx: 'N/A',
      secondaryDx: 'N/A',
      insuranceType: 'N/A',
    }));

    return [...existingPatients, ...newlyAddedPatients];
  }, [patients, removedPatientIds, addedPatients]);

  const effectiveTotalCount = totalCount - removedPatientIds.length + addedPatientIds.length;
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const handleSearch = () => {
    setCurrentPage(1);
    const searchFilters: any = {};
    
    // Build basic search filters
    const conditions: any[] = [];
    
    if (nameValue) {
      conditions.push({
        _or: [
          { first_name: { _ilike: `%${nameValue}%` } },
          { last_name: { _ilike: `%${nameValue}%` } }
        ]
      });
    }
    if (phoneValue) {
      conditions.push({ mobile_number: { _ilike: `%${phoneValue}%` } });
    }
    if (emrValue) {
      conditions.push({ emr_id: { _ilike: `%${emrValue}%` } });
    }
    
    // Add advanced filters from FilterModal
    if (appliedFilters.clinic_id?.length > 0) {
      conditions.push({ clinic_id: { _in: appliedFilters.clinic_id } });
    }
    if (appliedFilters.clinic_facility_id?.length > 0) {
      conditions.push({ clinic_facility_id: { _in: appliedFilters.clinic_facility_id } });
    }
    if (appliedFilters.provider_id?.length > 0) {
      conditions.push({ provider_id: { _in: appliedFilters.provider_id } });
    }
    if (appliedFilters.primary_insurance_payor?.length > 0) {
      conditions.push({ primary_insurance_payor: { _in: appliedFilters.primary_insurance_payor } });
    }
    if (appliedFilters.secondary_insurance_payor?.length > 0) {
      conditions.push({ secondary_insurance_payor: { _in: appliedFilters.secondary_insurance_payor } });
    }
    if (appliedFilters.condition?.length > 0) {
      conditions.push({ condition: { _in: appliedFilters.condition } });
    }
    if (appliedFilters.zip_code?.length > 0) {
      conditions.push({ zip_code: { _in: appliedFilters.zip_code } });
    }
    if (appliedFilters.program_eligibility?.length > 0) {
      conditions.push({ program_eligibility: { _in: appliedFilters.program_eligibility } });
    }
    
    // Combine all conditions
    if (conditions.length > 0) {
      searchFilters._and = conditions;
    }
    
    refetch(searchFilters);
  };

  const handleFilter = () => {
    setIsFilterModalOpen(true);
  };

  const handleClearFilters = () => {
    setNameValue("");
    setPhoneValue("");
    setEmrValue("");
    setAppliedFilters({});
    setCurrentPage(1);
    refetch({});
  };

  const handlePatientSelect = (patientId: string, checked: boolean) => {
    if (checked) {
      setSelectedPatients(prev => [...prev, patientId]);
    } else {
      setSelectedPatients(prev => prev.filter(id => id !== patientId));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    const allCurrentPageIds = displayPatients.map(p => p.id);
    
    if (checked) {
      setSelectedPatients(prev => [...new Set([...prev, ...allCurrentPageIds])]);
    } else {
      setSelectedPatients(prev => prev.filter(id => !allCurrentPageIds.includes(id)));
    }
  };

  const handleRemovePatients = () => {
    // Navigate to remove patients page for duplicate
    navigate(`/admin/patient-groups/duplicate/${patientGroupId}/remove-patients`);
  };

  const handleAddPatients = () => {
    // Navigate to add patients page for duplicate
    navigate(`/admin/patient-groups/duplicate/${patientGroupId}/add-patients`);
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) {
      toast({
        title: "Error",
        description: "Please enter a group name.",
        variant: "destructive",
      });
      return;
    }

    if (!user || !token) {
      toast({
        title: "Error",
        description: "Authentication required.",
        variant: "destructive",
      });
      return;
    }

    // Check if group name already exists
    try {
      const hasuraToken = localStorage.getItem("hasuraToken");
      if (!hasuraToken) {
        toast({
          title: "Error",
          description: "Authentication token not found.",
          variant: "destructive",
        });
        return;
      }

      const response = await groupsGraphqlService.checkGroupName(hasuraToken, newGroupName.trim());
      if (response.data?.pep_vw_group_details && response.data.pep_vw_group_details.length > 0) {
        toast({
          title: "Error",
          description: `Group name "${newGroupName.trim()}" already exists. Please choose a different name.`,
          variant: "destructive",
        });
        return;
      }
    } catch (error) {
      console.error('Error checking group name:', error);
      toast({
        title: "Error",
        description: "Failed to validate group name.",
        variant: "destructive",
      });
      return;
    }

    // Combine existing patient IDs (excluding removed) with added ones
    const existingPatientIds = patients
      .filter(p => !removedPatientIds.includes(p.enrollment_patient_id?.toString() || ''))
      .map(p => p.enrollment_patient_id);
    const allPatientIds = [...existingPatientIds, ...addedPatientIds.map(id => parseInt(id))];

    if (allPatientIds.length === 0) {
      toast({
        title: "Error",
        description: "Please include at least one patient in the group.",
        variant: "destructive",
      });
      return;
    }

    setIsCreating(true);
    try {
      const payload = {
        groupName: newGroupName.trim(),
        groupFilters: Object.keys(appliedFilters).length > 0 ? JSON.stringify(appliedFilters) : "{}",
        isArchived: false,
        patientIds: allPatientIds
      };

      const url = `${API_CONFIG.API_BASE}/api/ngpep/${user.id}/patient-group`;
      
      await axios.post(url, payload, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      // Clear session storage
      sessionStorage.removeItem(`duplicate_${patientGroupId}_removed`);
      sessionStorage.removeItem(`duplicate_${patientGroupId}_added`);
      sessionStorage.removeItem(`duplicate_${patientGroupId}_added_patients`);

      toast({
        title: "Success",
        description: `Group "${newGroupName}" created successfully with ${allPatientIds.length} patients.`,
      });

      navigate("/admin/patient-groups");
    } catch (error: any) {
      console.error('Error creating group:', error);
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to create patient group.",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-muted/30">
      <header className="bg-background border-b px-6 py-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/admin/patient-groups")}
            className="p-2"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-2xl font-semibold text-foreground">Duplicate Patient Group</h1>
        </div>
      </header>

      <main className="p-6 space-y-6">
        {/* Group Name Section with Action Buttons */}
        <div className="bg-background border rounded-lg p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Users className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <span className="text-sm font-medium text-muted-foreground">New Group Name</span>
              <Input
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                className="mt-1"
                placeholder="Enter new group name"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Duplicating from: {currentGroup?.group_name}
              </p>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={() => navigate("/admin/patient-groups")}
              disabled={isCreating}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleCreateGroup}
              disabled={isCreating || !newGroupName.trim()}
              className="bg-primary hover:bg-primary/90"
            >
              {isCreating ? "Creating..." : "Create Group"}
            </Button>
          </div>
        </div>

        {/* Filter Section */}
        <div className="bg-background border rounded-lg p-6">
          <FilterSection
            nameValue={nameValue}
            phoneValue={phoneValue}
            emrValue={emrValue}
            onNameChange={setNameValue}
            onPhoneChange={setPhoneValue}
            onEmrChange={setEmrValue}
            onSearch={handleSearch}
            onFilter={handleFilter}
          />
          
          {/* Clear Filters Button */}
          {(nameValue || phoneValue || emrValue || Object.keys(appliedFilters).length > 0) && (
            <div className="mt-4 flex justify-end">
              <Button 
                variant="outline" 
                onClick={handleClearFilters}
                className="text-destructive hover:text-destructive"
              >
                Clear All Filters
              </Button>
            </div>
          )}
        </div>

        {/* Patients Section */}
        <div className="bg-background border rounded-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-foreground">
              Patients in {newGroupName || 'New Group'} ({effectiveTotalCount})
            </h3>
            <div className="flex gap-3">
              <Button 
                variant="destructive" 
                onClick={handleRemovePatients}
                className="w-40"
              >
                Remove Patients
              </Button>
              <Button 
                onClick={handleAddPatients}
                className="w-40 bg-amber-500 hover:bg-amber-600 text-white"
              >
                Add Patients
              </Button>
            </div>
          </div>

          <PatientListTable
            patients={displayPatients}
            selectedPatients={selectedPatients}
            onPatientSelect={handlePatientSelect}
            onSelectAll={handleSelectAll}
            selectCount={0}
            onSelectCountChange={undefined}
            filteredCount={effectiveTotalCount}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      </main>

      <FilterModal
        open={isFilterModalOpen}
        onOpenChange={setIsFilterModalOpen}
        initialFilters={appliedFilters}
        onApply={(filters) => {
          setAppliedFilters(filters);
          setIsFilterModalOpen(false);
          // Trigger search after filters are applied
          setTimeout(() => handleSearch(), 100);
        }}
      />
    </div>
  );
}
